/**
 * Loss History Detail (#33939) — the Loss Summary rollup (display only).
 *
 * Owner rules:
 *  - ONE loss event per calendar day. Multiple claims filed on the same day — including the same
 *    claim surfaced by two writers (IMT import + A+ pull) — are a single loss. There is no
 *    dollar threshold on farm; every non-ignored claim participates.
 *  - We do NOT sum or compare paid amounts across claims. Paid Amount / Loss Amount are per-claim
 *    values shown on each entry, not summary aggregates — so there is no summed "Total Paid" or
 *    "average severity" here.
 *
 * The summary therefore reports event counts only: total loss days, per-window frequency (distinct
 * loss days), and a by-cause breakdown (one representative cause per day). This is a different basis
 * from the claim-free RATING input (see ./rating), which is recency-based — that's expected.
 */
import { LOSS_SUMMARY_WINDOWS } from './constants';
import { normalizeLossDate } from './normalize';
import type { LossHistoryEntry } from './types';

export interface LossSummary {
  /** Number of distinct non-ignored loss days (one loss event per calendar day). */
  totalLosses: number;
  /** Count of loss days per cause of loss (one representative cause per day). */
  byCause: Record<string, number>;
  /** Distinct loss days within each lookback window (e.g. { 3: n, 5: n }). */
  frequency: Record<number, number>;
}

interface LossDay {
  /** Normalized ISO day, or null for an undated entry (counted but not window-eligible). */
  day: string | null;
  cause: string;
}

/**
 * Collapse entries to one loss event per calendar day. Same-day claims (any amount, any source)
 * are a single loss; the day's representative cause is the first entry seen for that day. Undated
 * entries can't be a dated event, so each is kept as its own loss keyed by id.
 */
function collapseToLossDays(entries: LossHistoryEntry[]): LossDay[] {
  const byKey = new Map<string, LossDay>();
  entries.forEach((entry, index) => {
    const day = normalizeLossDate(entry.lossDate);
    const key = day ?? `id:${entry.id ?? `idx-${index}`}`;
    if (!byKey.has(key)) {
      byKey.set(key, { day, cause: entry.causeOfLoss || 'Unknown' });
    }
  });
  return [...byKey.values()];
}

const toTime = (iso: string): number | null => {
  const time = new Date(`${iso}T00:00:00Z`).getTime();
  return Number.isNaN(time) ? null : time;
};

export function summarizeLossHistory(
  entries: LossHistoryEntry[] | null | undefined,
  referenceDate: string | null | undefined
): LossSummary {
  const days = collapseToLossDays((entries || []).filter((entry) => entry && !entry.ignore));
  const totalLosses = days.length;

  const byCause = days.reduce<Record<string, number>>((acc, lossDay) => {
    acc[lossDay.cause] = (acc[lossDay.cause] || 0) + 1;
    return acc;
  }, {});

  const refIso = normalizeLossDate(referenceDate);
  const refTime = refIso ? toTime(refIso) : null;
  const frequency = LOSS_SUMMARY_WINDOWS.reduce<Record<number, number>>((acc, years) => {
    if (refTime === null) {
      acc[years] = 0;
      return acc;
    }
    const cutoff = new Date(refTime);
    cutoff.setUTCFullYear(cutoff.getUTCFullYear() - years);
    acc[years] = days.filter((lossDay) => {
      if (!lossDay.day) {
        return false;
      }
      const time = toTime(lossDay.day);
      return time !== null && time >= cutoff.getTime() && time <= refTime;
    }).length;
    return acc;
  }, {});

  return { totalLosses, byCause, frequency };
}
